'use client';

import { Box, freddyTheme, Grid, Link, Typography } from '@coolblue-development/freddy';
import selectAlternativeTrigger from '@coolblue-development/ga4-events/triggers/AlternativePrimaryItem/selectAlternativeTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import {
  ProductCard as ProductCardComponent,
  ProductTitle,
  ProductTitleTitle
} from '@coolblue-development/next-patterns/client';
import type { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import type { ReactElement } from 'react';

import { PromotionalProductImageVariant } from './PromotionalProductVariant';

export type ProductCardProps = {
  product: Product;
  isCurrentProduct: boolean;
  cbcLabel?: ReactElement;
  trackingProps: {
    totalIndices: number;
    index: number;
  };
};

const ProductCard = ({ product, isCurrentProduct, cbcLabel, trackingProps }: ProductCardProps) => {
  const { t } = useTranslation('webshop');
  const { trackEvent } = useAnalytics();

  return (
    <Grid
      templateColumns='1fr'
      autoRows='auto'
      component='li'
      px={300}
      sx={{
        alignContent: 'start',

        '&:first-of-type': {
          backgroundColor: freddyTheme.color.background.secondary,
          borderRadius: `${freddyTheme.borderRadius[100]} ${freddyTheme.borderRadius[100]} 0 0`
        }
      }}
    >
      <Typography mt={300} textAlign='center'>
        {isCurrentProduct ? t('betterOptionsBlock.currentProductText') : <>&nbsp;</>}
      </Typography>
      {/* Hotfix to hide the empty SalesPrice slot and the gap,
        we also pass an empty fragment to the slot */}
      <Box mt={200} sx={{ '& > div > div:last-child': { display: 'none' } }}>
        <ProductCardComponent.Generic
          promotionalProductImage={
            <PromotionalProductImageVariant
              cbcLabel={cbcLabel}
              isCurrentProduct={isCurrentProduct}
              product={product}
              trackingProps={trackingProps}
            />
          }
          productTitle={
            <ProductTitle>
              <ProductTitleTitle>
                {isCurrentProduct ? (
                  <Typography
                    variant='h3'
                    fontWeight={freddyTheme.typography.fontWeight.openSans.bold}
                  >
                    {product.name}
                  </Typography>
                ) : (
                  <Link
                    fontWeight={freddyTheme.typography.fontWeight.openSans.bold}
                    href={product?.urls?.product}
                    onClick={() =>
                      trackEvent(
                        selectAlternativeTrigger({
                          index: trackingProps.index,
                          total_indices: trackingProps.totalIndices,
                          method: 'title',
                          item_id: product.id
                        })
                      )
                    }
                  >
                    {product.name}
                  </Link>
                )}
              </ProductTitleTitle>
            </ProductTitle>
          }
          salesPrice={<></>}
        />
      </Box>
    </Grid>
  );
};

export { ProductCard };
